import React, { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../ui/dialog';
import { useNavigate } from 'react-router-dom';
import { Settings, Edit, AlertCircle, Loader2, Wrench, Save } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ModalOperacoesLote({
  open,
  onOpenChange,
  lote,
  operacoes = [],
  loading = false,
  operadores = [],
  onEditOperacoes,
  ofId,
  onUpdated,
}) {
  const navigate = useNavigate();
  const isArrLote = lote?.categoria_codigo === 'ARR' || lote?.origem === 'arranjo';
  // Lote iniciado = qualquer operação tem produção ou estado != 'pendente'
  const loteIniciado = (() => {
    const estados = lote?.operacoes_estado || [];
    return estados.some(e => (e.quantidade_produzida || 0) > 0 || (e.status && e.status !== 'pendente'));
  })();
  const [opsOf, setOpsOf] = useState([]);
  const [maeOpInfo, setMaeOpInfo] = useState(null); // { id, codigo, nome, is_contagem } — read-only display
  const [permitidas, setPermitidas] = useState(new Set());
  const [opMae, setOpMae] = useState('');
  const [savingPerm, setSavingPerm] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (!open || !isArrLote || !ofId) return;
    setSaveError('');
    // permitidas = apenas as ADICIONAIS (sem a mãe). A mãe é mantida read-only em opMae.
    const maeId = lote?.operacao_mae_id || '';
    const permsRaw = lote?.operacoes_permitidas || [];
    const adicionais = new Set(permsRaw.filter(id => id !== maeId));
    setPermitidas(adicionais);
    setOpMae(maeId);
    Promise.all([
      axios.get(`${API}/ofs/${ofId}`),
      axios.get(`${API}/operacoes`)
    ])
      .then(([resOf, resOps]) => {
        const allOps = Array.isArray(resOps.data) ? resOps.data : [];
        const opsMap = new Map(allOps.map(o => [o.id, o]));
        const opsAtrib = resOf.data?.operacoes_atribuidas || resOf.data?.operacoes || [];
        const ops = opsAtrib.map(o => {
          const id = o.operacao_id || o.id;
          const ref = opsMap.get(id) || {};
          return {
            id,
            codigo: o.codigo || o.operacao_codigo || ref.codigo || '',
            nome: o.nome || o.operacao_nome || ref.nome || '',
            ordem: o.ordem || 0,
            is_contagem: !!(o.is_contagem || ref.is_contagem),
            categoria_codigo: o.categoria_codigo || ref.categoria_codigo || '',
          };
        }).filter(o => o.id).sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
        // Excluir ops ARR — operação mãe é separada
        const opsNaoArr = ops.filter(o => o.categoria_codigo !== 'ARR');
        setOpsOf(opsNaoArr);
        // Construir info read-only da operação mãe
        if (maeId) {
          const maeRef = opsMap.get(maeId) || {};
          setMaeOpInfo({
            id: maeId,
            codigo: maeRef.codigo || '',
            nome: maeRef.nome || '(operação não encontrada)',
            is_contagem: !!maeRef.is_contagem,
          });
        } else {
          setMaeOpInfo(null);
        }
        // Default só se não houver lista guardada
        if (!(lote?.operacoes_permitidas || []).length) {
          setPermitidas(new Set(opsNaoArr.map(o => o.id).filter(id => id !== maeId)));
        }
      })
      .catch(() => { setOpsOf([]); setMaeOpInfo(null); });
  }, [open, isArrLote, ofId, lote]);

  const togglePermitida = (id) => {
    if (loteIniciado) return;
    setSaveError('');
    const novo = new Set(permitidas);
    if (novo.has(id)) novo.delete(id); else novo.add(id);
    setPermitidas(novo);
  };

  const guardarPermitidas = async () => {
    if (!ofId || !lote?.id) return;
    setSaveError('');
    if (loteIniciado) {
      const msg = 'Não é possível editar — o lote já foi iniciado.';
      setSaveError(msg);
      toast.error(msg);
      return;
    }
    // Lotes criados sem operação mãe (operacao_mae_id null/undefined) — exigir pelo menos 1 adicional
    const semMae = !opMae;
    if (semMae && permitidas.size === 0) {
      const msg = 'Selecione pelo menos uma operação adicional (lote sem operação mãe).';
      setSaveError(msg);
      toast.error(msg);
      return;
    }
    setSavingPerm(true);
    try {
      // No payload incluir mãe + adicionais (backend espera lista completa)
      const payloadPerms = [...permitidas];
      if (opMae && !payloadPerms.includes(opMae)) payloadPerms.push(opMae);
      await axios.put(`${API}/ofs/${ofId}/lotes/${lote.id}/operacoes-permitidas`, {
        operacoes_permitidas: payloadPerms,
        operacao_mae_id: opMae || null,
      });
      toast.success(`Operações actualizadas (mãe: ${opMae ? '1' : '0'} + extras: ${permitidas.size})`);
      if (onUpdated) onUpdated();
      onOpenChange(false);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erro ao guardar';
      setSaveError(msg);
      toast.error(msg);
    } finally {
      setSavingPerm(false);
    }
  };

  const formatarNomeOperador = (operadorId, nome) => {
    const operador = operadores.find((o) => o.id === operadorId);
    if (operador) {
      const nomes = (operador.nome || '').split(' ');
      if (nomes.length >= 2) return `${nomes[0]} ${nomes[nomes.length - 1]}`;
      return operador.nome || operador.username;
    }
    return nome || 'N/A';
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-blue-600" />
            Operações do Lote {lote?.codigo_lote || `L${lote?.numero_lote}`}
          </DialogTitle>
          <DialogDescription>
            Visualize e acompanhe o progresso das operações deste lote
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {isArrLote && loteIniciado && (
            <div className="mb-3 p-3 rounded-lg border-2 border-amber-300 bg-amber-50 flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-bold text-amber-900">Lote já iniciado — edição bloqueada</p>
                <p className="text-amber-700 text-xs mt-0.5">A edição da Operação Mãe e das operações permitidas só é possível enquanto nenhuma operação tiver sido iniciada. Cancele os registos em curso para editar.</p>
              </div>
            </div>
          )}
          {isArrLote && (
            <div className={`mb-4 p-3 rounded-lg border-2 ${maeOpInfo ? 'border-rose-300 bg-rose-50' : 'border-stone-300 bg-stone-50'}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-[10px] px-1.5 py-0.5 rounded text-white font-extrabold ${maeOpInfo ? 'bg-rose-600' : 'bg-stone-400'}`}>A</span>
                <p className={`font-bold text-sm ${maeOpInfo ? 'text-rose-900' : 'text-stone-500 line-through'}`}>
                  Operação Mãe — {maeOpInfo ? 'definida' : 'desactivada'}
                </p>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-stone-200 text-stone-700 font-semibold">definida na criação · não editável</span>
              </div>
              {maeOpInfo ? (
                <>
                  <p className="text-[11px] text-rose-800/80 mb-2">A primeira operação executada no lote — tem de ser concluída antes das adicionais. Para alterar, elimine este lote e crie novo.</p>
                  <div className="flex items-center gap-2 px-3 py-2 rounded-md border-2 border-rose-400 bg-white" data-testid="mae-readonly">
                    <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-rose-600 text-white flex-shrink-0">
                      <span className="block h-2 w-2 rounded-full bg-white"/>
                    </span>
                    <span className="text-[11px] font-mono font-bold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">{maeOpInfo.codigo || '—'}</span>
                    <span className="text-sm font-semibold text-stone-800">{maeOpInfo.nome}</span>
                    {maeOpInfo.is_contagem && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded font-bold bg-blue-100 text-blue-700 ml-auto">CONT</span>
                    )}
                  </div>
                </>
              ) : (
                <div className="px-3 py-2 rounded-md border border-stone-300 bg-white text-xs text-stone-700" data-testid="mae-none">
                  <span className="font-semibold">Lote criado sem operação mãe</span> — as operações adicionais ficam disponíveis imediatamente ao operador. Pode editar livremente o Bloco B abaixo.
                </div>
              )}
            </div>
          )}
          {isArrLote && (
            <div className="mb-4 p-3 rounded-lg border-2 border-stone-200 bg-stone-50">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-stone-700 text-white font-extrabold">B</span>
                  <p className="font-bold text-sm text-stone-800">Operações adicionais</p>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setPermitidas(new Set(opsOf.map(o => o.id)))} className="h-6 text-[11px]">Todas</Button>
                  <Button size="sm" variant="ghost" onClick={() => setPermitidas(new Set())} className="h-6 text-[11px]">Nenhuma</Button>
                </div>
              </div>
              <p className="text-[11px] text-stone-600 mb-2">{maeOpInfo ? 'Operações da OF (não-ARR) disponíveis após a mãe estar concluída. Agrupadas por categoria.' : 'Operações da OF (não-ARR) que ficam disponíveis ao operador. Agrupadas por categoria.'}</p>
              {opsOf.length === 0 ? (
                <p className="text-center text-xs text-stone-500 py-3">Nenhuma operação adicional</p>
              ) : (() => {
                // Agrupar por categoria_codigo
                const grupos = {};
                opsOf.forEach(op => {
                  const cat = op.categoria_codigo || 'sem-categoria';
                  if (!grupos[cat]) grupos[cat] = [];
                  grupos[cat].push(op);
                });
                const catKeys = Object.keys(grupos).sort();
                return (
                  <div className="space-y-3 max-h-80 overflow-y-auto">
                    {catKeys.map(catCodigo => {
                      const opsCat = grupos[catCodigo];
                      const idsCat = opsCat.map(o => o.id);
                      const todasMarcadas = idsCat.every(id => permitidas.has(id));
                      const algumasMarcadas = idsCat.some(id => permitidas.has(id));
                      const toggleCat = (marcar) => {
                        const novo = new Set(permitidas);
                        if (marcar) idsCat.forEach(id => novo.add(id));
                        else idsCat.forEach(id => novo.delete(id));
                        setPermitidas(novo);
                      };
                      return (
                        <div key={catCodigo} className="rounded border border-stone-200 bg-white p-2">
                          <div className="flex items-center justify-between mb-1.5 pb-1.5 border-b border-stone-100">
                            <span className="text-[10px] font-extrabold tracking-wide text-stone-700">{catCodigo === 'sem-categoria' ? 'SEM CATEGORIA' : catCodigo}</span>
                            <div className="flex gap-1">
                              <button
                                type="button"
                                onClick={() => toggleCat(true)}
                                disabled={todasMarcadas}
                                className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200 disabled:opacity-40 font-semibold"
                                data-testid={`mod-cat-todas-${catCodigo}`}
                              >Todas</button>
                              <button
                                type="button"
                                onClick={() => toggleCat(false)}
                                disabled={!algumasMarcadas}
                                className="text-[10px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-700 hover:bg-stone-200 disabled:opacity-40 font-semibold"
                                data-testid={`mod-cat-nenhuma-${catCodigo}`}
                              >Nenhuma</button>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                            {opsCat.map(op => {
                              const sel = permitidas.has(op.id);
                              return (
                                <button
                                  key={op.id}
                                  type="button"
                                  onClick={() => togglePermitida(op.id)}
                                  className={`text-left flex items-center gap-2 px-2.5 py-1.5 rounded-md border transition ${sel ? 'bg-emerald-50 border-emerald-400' : 'bg-white border-stone-200 hover:border-stone-400'}`}
                                  data-testid={`arr-op-perm-${op.id}`}
                                >
                                  <span className={`inline-flex items-center justify-center h-4 w-4 rounded border flex-shrink-0 ${sel ? 'bg-emerald-600 border-emerald-700 text-white' : 'border-stone-300'}`}>
                                    {sel && <span className="text-[9px] font-extrabold">✓</span>}
                                  </span>
                                  <span className="text-[10px] font-mono font-bold px-1 py-0.5 rounded bg-stone-100 text-stone-700">{op.codigo}</span>
                                  <span className="text-xs font-semibold text-stone-800 truncate">{op.nome}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
              <div className="flex items-center justify-between mt-3 pt-2 border-t border-stone-200">
                <p className="text-[11px] text-stone-700"><strong>{permitidas.size}</strong> adicionais + {opMae ? '1 mãe' : 'sem mãe'}</p>
                <Button size="sm" onClick={guardarPermitidas} disabled={savingPerm || loteIniciado} className="bg-rose-600 hover:bg-rose-700 text-white h-7 text-xs" data-testid="btn-guardar-ops-arr">
                  <Save className="h-3 w-3 mr-1" />
                  {savingPerm ? 'A guardar...' : 'Guardar regras'}
                </Button>
              </div>
              {saveError && (
                <div
                  className="mt-2 p-2 rounded-md border-2 border-red-300 bg-red-50 flex items-start gap-2"
                  data-testid="ops-perm-save-error"
                  role="alert"
                >
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-red-800 font-semibold">
                    {saveError}
                  </div>
                </div>
              )}
            </div>
          )}
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-navy" />
            </div>
          ) : operacoes.length === 0 ? (
            <div className="text-center py-8 text-stone-500">
              <AlertCircle className="h-12 w-12 mx-auto mb-3 text-stone-300" />
              <p>Nenhuma operação atribuída a este lote.</p>
              <p className="text-sm mt-2">Adicione operadores às operações da OF para visualizar aqui.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Resumo do lote */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-stone-50 rounded-lg">
                <div>
                  <p className="text-xs text-stone-500">Categoria</p>
                  <p className="font-medium">{lote?.categoria_nome || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-stone-500">Quantidade</p>
                  <p className="font-medium">{lote?.quantidade || 0} peças</p>
                </div>
                <div>
                  <p className="text-xs text-stone-500">Progresso</p>
                  <p className="font-medium">{lote?.resumo_operacoes?.percentagem || 0}%</p>
                </div>
              </div>

              {/* Lista de operações */}
              {(() => {
                // Em lotes ARR, mostrar apenas a mãe + adicionais permitidas
                let opsExibidas = operacoes;
                if (isArrLote) {
                  const permIds = new Set([...permitidas, opMae].filter(Boolean));
                  if (permIds.size > 0) {
                    opsExibidas = operacoes.filter(op => permIds.has(op.operacao_id || op.id));
                  }
                }
                return (
              <div className="space-y-2">
                <h4 className="font-medium text-stone-700">Operações ({opsExibidas.length})</h4>
                {opsExibidas.map((op, idx) => {
                  const progresso =
                    op.quantidade_produzida && lote?.quantidade
                      ? Math.round((op.quantidade_produzida / lote.quantidade) * 100)
                      : 0;
                  const status = op.status || op.estado || 'pendente';

                  return (
                    <div key={op.operacao_id || op.id || idx} className="border rounded-lg p-3 bg-white">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-navy text-white text-xs flex items-center justify-center">
                            {idx + 1}
                          </span>
                          <span className="font-medium">{op.codigo}</span>
                          <span className="text-stone-500">-</span>
                          <span className="text-stone-700">{op.nome || op.operacao_nome}</span>
                        </div>
                        <span
                          className={`px-2 py-0.5 rounded text-xs ${
                            status === 'concluida' || status === 'concluido'
                              ? 'bg-green-100 text-green-700'
                              : status === 'em_curso'
                              ? 'bg-blue-100 text-blue-700'
                              : status === 'parcial'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-stone-100 text-stone-600'
                          }`}
                        >
                          {status === 'concluida' || status === 'concluido'
                            ? 'Concluída'
                            : status === 'em_curso'
                            ? 'Em Curso'
                            : status === 'parcial'
                            ? 'Parcial'
                            : 'Pendente'}
                        </span>
                      </div>

                      {/* Barra de progresso */}
                      <div className="h-2 bg-stone-200 rounded-full overflow-hidden mb-2">
                        <div
                          className={`h-full transition-all ${
                            status === 'concluida' || status === 'concluido'
                              ? 'bg-green-500'
                              : status === 'em_curso'
                              ? 'bg-blue-500'
                              : status === 'parcial'
                              ? 'bg-amber-500'
                              : 'bg-stone-300'
                          }`}
                          style={{ width: `${progresso}%` }}
                        />
                      </div>

                      <div className="flex justify-between text-xs text-stone-500">
                        <span>
                          {op.quantidade_produzida || 0} / {lote?.quantidade || 0} peças ({progresso}%)
                        </span>
                        {op.ultimo_operador_id && (
                          <span className="text-stone-600 font-medium">
                            Operador: {formatarNomeOperador(op.ultimo_operador_id, op.operador_nome)}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
                );
              })()}
            </div>
          )}
        </div>

        <DialogFooter>
          {!isArrLote && (
            <div className="flex gap-2 mr-auto">
              <Button
                variant="outline"
                onClick={() => {
                  onOpenChange(false);
                  onEditOperacoes && onEditOperacoes();
                }}
                className="flex items-center gap-2 border-blue-300 text-blue-700 hover:bg-blue-50"
                data-testid="btn-gerir-operacoes-lote"
              >
                <Settings className="h-4 w-4" />
                Gerir Operações
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/gestor/operacoes')}
                className="flex items-center gap-2"
                data-testid="btn-ir-editar-operacoes"
              >
                <Edit className="h-4 w-4" />
                Editar Operações
              </Button>
            </div>
          )}
          {isArrLote && (
            <p className="text-[11px] text-rose-700 mr-auto self-center max-w-md">
              <Wrench className="h-3 w-3 inline mr-1" />
              Em Lotes ARR só se permite/restringe operações desta lista — não edita as operações globais da OF.
            </p>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
